import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { Plus, Trash2, Ticket } from "lucide-react";

const empty = { code: "", description: "", kind: "percent", value: 10, min_order: 0, max_discount: "", usage_limit: "", expires_at: "", active: true };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: +form.value,
        min_order: +form.min_order,
        max_discount: form.max_discount === "" ? null : +form.max_discount,
        usage_limit: form.usage_limit === "" ? null : +form.usage_limit,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      if (editing) await api.put(`/admin/coupons/${editing}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success("Coupon saved");
      setForm(empty);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const edit = (c) => {
    setEditing(c.id);
    setForm({
      code: c.code,
      description: c.description || "",
      kind: c.kind,
      value: c.value,
      min_order: c.min_order || 0,
      max_discount: c.max_discount ?? "",
      usage_limit: c.usage_limit ?? "",
      expires_at: c.expires_at ? c.expires_at.slice(0, 16) : "",
      active: c.active,
    });
  };

  const del = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await api.delete(`/admin/coupons/${c.id}`);
    load();
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
        <Ticket className="w-6 h-6 text-hyd-gold" /> Coupons
      </h1>
      <p className="text-hyd-text2 text-sm">Create % or flat discount codes with limits and expiry.</p>

      <div className="grid md:grid-cols-5 gap-5 mt-5">
        <div className="md:col-span-3 bg-hyd-panel border border-hyd-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-hyd-panel2 text-hyd-text2 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Value</th>
                <th className="text-left p-3">Min</th>
                <th className="text-left p-3">Used</th>
                <th className="text-left p-3">Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hyd-border">
              {coupons.map((c) => (
                <tr key={c.id}>
                  <td className="p-3 font-mono font-bold text-hyd-gold">{c.code}</td>
                  <td className="p-3">{c.kind === "percent" ? `${c.value}%` : `₹${c.value}`}{c.max_discount ? <span className="text-xs text-hyd-text3"> (max ₹{c.max_discount})</span> : null}</td>
                  <td className="p-3">₹{c.min_order || 0}</td>
                  <td className="p-3">{c.used_count || 0}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</td>
                  <td className="p-3 text-xs text-hyd-text2">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => edit(c)} className="text-hyd-gold hover:bg-hyd-panel">Edit</Button>
                    <Button data-testid={ADMIN.couponDelete(c.id)} size="sm" variant="ghost" onClick={() => del(c)} className="text-hyd-red hover:bg-hyd-panel"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {coupons.length === 0 && <div className="p-8 text-center text-hyd-text3">No coupons yet.</div>}
        </div>

        <form onSubmit={submit} className="md:col-span-2 bg-hyd-panel border border-hyd-border rounded-xl p-4 space-y-3 h-fit">
          <h3 className="font-display text-lg font-bold">{editing ? "Edit" : "New"} coupon</h3>
          <F label="Code (e.g. DIWALI25)">
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required />
          </F>
          <F label="Description">
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Type">
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="bg-hyd-bg border-hyd-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent %</SelectItem>
                  <SelectItem value="flat">Flat ₹</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label={form.kind === "percent" ? "Value %" : "Value ₹"}>
              <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} required />
            </F>
            <F label="Min order ₹">
              <Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} />
            </F>
            <F label="Max discount ₹">
              <Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder="optional" />
            </F>
            <F label="Usage limit">
              <Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="optional" />
            </F>
            <F label="Expires">
              <Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
            </F>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} /> Active
          </label>
          <Button data-testid={ADMIN.couponSave} type="submit" className="w-full bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold">
            {editing ? "Save" : (<><Plus className="w-4 h-4 mr-1" />Create</>)}
          </Button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="w-full text-xs text-hyd-text2 hover:text-hyd-gold">Cancel</button>
          )}
        </form>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-hyd-text2 mb-1 block">{label}</Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text [&_textarea]:bg-hyd-bg [&_textarea]:border-hyd-border [&_textarea]:text-hyd-text">{children}</div>
    </div>
  );
}
